/** The Shipyard: owning, earning and buying hulls, and their multipliers (GDD §6.1). */

import { SHIPS, shipDef, type ShipEffect, type ShipGoal } from '../content/ships';
import type { Emit } from './events';
import type { GameState } from './state';

export function ownsShip(state: GameState, id: string): boolean {
  return state.ships.includes(id);
}

/** ×multiplier from every owned hull for one effect (1 with none). */
export function shipMult(state: GameState, effect: ShipEffect): number {
  let mult = 1;
  for (const id of state.ships) mult *= shipDef(id).mults[effect] ?? 1;
  return mult;
}

/** Progress toward an earned hull's goal: [have, need]. */
export function goalProgress(state: GameState, goal: ShipGoal): [number, number] {
  if (goal.kind === 'depth') return [state.bestDepth, goal.planets];
  if (goal.kind === 'folds') return [state.folds, goal.folds];
  return [state.challengesDone, goal.challenges];
}

export function canBuyShip(state: GameState, id: string): boolean {
  const def = shipDef(id);
  return def.source.kind === 'sc' && !ownsShip(state, id) && state.sc >= def.source.cost;
}

export function buyShip(state: GameState, id: string): boolean {
  const def = shipDef(id);
  if (!canBuyShip(state, id) || def.source.kind !== 'sc') return false;
  state.sc -= def.source.cost;
  state.ships.push(id);
  return true;
}

/** Store hulls: granted by a purchase (dev only in the prototype — there is no store). */
export function grantShip(state: GameState, id: string): boolean {
  const def = shipDef(id);
  if (ownsShip(state, id)) return false;
  state.ships.push(id);
  if (def.source.kind === 'store') state.sc += def.source.sc;
  return true;
}

/** Records how deep the main galaxy has gone, and hands over any hull whose goal is now met. */
export function stepShips(state: GameState, emit: Emit): void {
  const main = state.run.challenge ? state.challenge?.other : state.run;
  if (main) {
    let depth = 0;
    main.planets.forEach((p, i) => { if (p.owned) depth = i + 1; });
    if (depth > state.bestDepth) state.bestDepth = depth;
  }
  for (const def of SHIPS) {
    if (def.source.kind !== 'earned' || ownsShip(state, def.id)) continue;
    const [have, need] = goalProgress(state, def.source.goal);
    if (have < need) continue;
    state.ships.push(def.id);
    emit({ kind: 'ship', id: def.id });
  }
}
